import {useMemo} from 'react'
import {Button} from '@primer/react'

import type {AutomatedComment, Thread} from '../../conversation-types'
import {AutomatedCommentSource} from '../../conversation-types'
import type {LineKey} from '../../types'
import {useDismissAutomatedReviewThreadMutation} from '@github-ui/pull-requests/use-dismiss-automated-review-thread-mutation'
import {useReopenAutomatedReviewThreadMutation} from '@github-ui/pull-requests/use-reopen-automated-review-thread-mutation'

function useAutomatedReviewThreadActions(automatedComment: AutomatedComment) {
  return useMemo(() => {
    const isDismissed = automatedComment.isDismissed
    const isDismissable = !isDismissed && automatedComment.viewerCanDismiss
    const isReopenable = isDismissed && automatedComment.viewerCanReopen

    let resourceName = 'alert'
    if (automatedComment.source === AutomatedCommentSource.CodeQuality) {
      resourceName = 'finding'
    }
    const toggleButtonLabel = isDismissed ? `Reopen ${resourceName}` : `Dismiss ${resourceName}`

    return {
      isDismissed,
      isDismissable,
      isReopenable,
      resourceName,
      toggleButtonLabel,
    }
  }, [automatedComment])
}

export function InlineAutomatedReviewThreadActions({
  automatedComment,
  thread,
  filePath,
  lineMarkersKey,
  setErrorMessage,
  setIsCollapsed,
  safeLocalStorage,
}: {
  automatedComment: AutomatedComment
  thread: Thread
  filePath: string
  lineMarkersKey: LineKey
  setErrorMessage: (message: string) => void
  setIsCollapsed: (isCollapsed: boolean) => void
  safeLocalStorage: {removeItem: (key: string) => void}
}) {
  const {isDismissable, isReopenable, resourceName, toggleButtonLabel} =
    useAutomatedReviewThreadActions(automatedComment)

  const {mutate: dismissAutomatedReviewThreadMutation} = useDismissAutomatedReviewThreadMutation(
    filePath,
    lineMarkersKey,
  )
  const handleDismissAutomatedReviewThread = () => {
    dismissAutomatedReviewThreadMutation(
      {automatedReviewCommentId: automatedComment.id, threadId: thread.id, reason: 'WONT_FIX'},
      {
        onError: () => setErrorMessage(`Failed to dismiss ${resourceName}`),
        onSuccess: () => {
          setIsCollapsed(true)
          safeLocalStorage.removeItem(`reviewThreadIsCollapsed_${thread.id}`)
        },
      },
    )
  }

  const {mutate: reopenAutomatedReviewThreadMutation} = useReopenAutomatedReviewThreadMutation(filePath, lineMarkersKey)
  const handleReopenAutomatedReviewThread = () => {
    reopenAutomatedReviewThreadMutation(
      {automatedReviewCommentId: automatedComment.id, threadId: thread.id},
      {
        onError: () => setErrorMessage(`Failed to reopen ${resourceName}`),
        onSuccess: () => {
          setIsCollapsed(false)
          safeLocalStorage.removeItem(`reviewThreadIsCollapsed_${thread.id}`)
        },
      },
    )
  }

  return (
    <>
      {isDismissable && (
        <div className="d-flex flex-items-center p-2 border-top flex-wrap">
          <Button aria-label={toggleButtonLabel} className="mr-2" onClick={handleDismissAutomatedReviewThread}>
            {toggleButtonLabel}
          </Button>
          <span className="fgColor-muted f6">
            Dismissing the {resourceName} will mark this conversation as resolved.
          </span>
        </div>
      )}
      {isReopenable && (
        <div className="d-flex flex-items-center p-2 border-top flex-wrap">
          <Button aria-label={toggleButtonLabel} className="mr-2" onClick={handleReopenAutomatedReviewThread}>
            {toggleButtonLabel}
          </Button>
        </div>
      )}
    </>
  )
}

try{ InlineAutomatedReviewThreadActions.displayName ||= 'InlineAutomatedReviewThreadActions' } catch {}